import { instanceOf } from './classes';

function currentPrefix() {
    return location.pathname.match(/^\/([a-z]+)\//)?.[1];
}

export function initializeWorkerScript(prefix?: string) {
    prefix ??= currentPrefix();
    if (!prefix) {
        throw new Error('Cannot find prefix in URL');
    }
    return `/${prefix}/workerEntrypoint.js`;
}

export function registerServiceWorker(prefix?: string) {
    prefix ??= currentPrefix();
    const script = initializeWorkerScript(prefix);
    return navigator.serviceWorker.register(script, {
        scope: `/${prefix}/`,
        type: 'module',
    });
}

export function isDedicatedWorker(
    self: any
): self is DedicatedWorkerGlobalScope {
    return instanceOf(self, 'DedicatedWorkerGlobalScope');
}

export function isServiceWorker(self: any): self is ServiceWorkerGlobalScope {
    return instanceOf(self, 'ServiceWorkerGlobalScope');
}

export interface ServiceWorkerListener {
    skipWaiteInstall?: boolean;
    claimChangeController?: boolean;

    install?(ev: ExtendableEvent): Promise<void>;
    activate?(ev: ExtendableEvent): Promise<void>;
    message?(ev: ExtendableMessageEvent): Promise<void>;
    messageError?(ev: MessageEvent): void;
    fetch?(ev: FetchEvent): Promise<void>;

    push?(ev: PushEvent): Promise<void>;
    pushSubscriptionChange?(ev: Event): void;
    notificationClick?(ev: NotificationEvent): Promise<void>;
    notificationClose?(ev: NotificationEvent): Promise<void>;

    error?(ev: ErrorEvent): void;
    languageChange?(ev: Event): void;
    online?(ev: Event): void;
    offline?(ev: Event): void;
    unhandledRejection?(ev: PromiseRejectionEvent): void;
    rejectionHandled?(ev: PromiseRejectionEvent): void;
}

export function setupServiceWorker(
    swGlobal: ServiceWorkerGlobalScope,
    listener: ServiceWorkerListener
) {
    function listenEvent<K extends keyof ServiceWorkerGlobalScopeEventMap>(
        key: K,
        listener: (ev: ServiceWorkerGlobalScopeEventMap[K]) => Promise<void>
    ) {
        swGlobal.addEventListener(key, (ev) => {
            if (ev instanceof ExtendableEvent) {
                ev.waitUntil(listener(ev));
            } else {
                listener(ev);
            }
        });
    }

    listenEvent('install', async (ev) => {
        if (listener.skipWaiteInstall ?? true) {
            await swGlobal.skipWaiting();
        }
        await listener.install?.(ev);
    });
    listenEvent('activate', async (ev) => {
        if (listener.claimChangeController ?? true) {
            await swGlobal.clients.claim();
        }
        await listener.activate?.(ev);
    });

    listenEvent('message', async (ev) => listener.message?.(ev));
    listenEvent('messageerror', async (ev) => listener.messageError?.(ev));
    listenEvent('fetch', async (ev) => listener.fetch?.(ev));
    listenEvent('push', async (ev) => listener.push?.(ev));
    listenEvent('pushsubscriptionchange', async (ev) =>
        listener.pushSubscriptionChange?.(ev)
    );
    listenEvent('notificationclick', async (ev) =>
        listener.notificationClick?.(ev)
    );
    listenEvent('notificationclose', async (ev) =>
        listener.notificationClose?.(ev)
    );

    listenEvent('error', async (ev) => listener.error?.(ev));
    listenEvent('unhandledrejection', async (ev) =>
        listener.unhandledRejection?.(ev)
    );
    listenEvent('rejectionhandled', async (ev) =>
        listener.rejectionHandled?.(ev)
    );

    listenEvent('languagechange', async (ev) => listener.languageChange?.(ev));
    listenEvent('online', async (ev) => listener.online?.(ev));
    listenEvent('offline', async (ev) => listener.offline?.(ev));
}
